import React from "react";
import { CLINIC, CLINIC_LOGO } from "@/lib/content";

const NAV = [
    { label: "About", href: "#about" },
    { label: "Services", href: "#services" },
    { label: "Doctor", href: "#doctors" },
    { label: "Technology", href: "#technology" },
    { label: "Cases", href: "#cases" },
    { label: "Reviews", href: "#reviews" },
    { label: "FAQ", href: "#faq" },
    { label: "Contact", href: "#contact" },
];

export default function Navbar({ onBook }) {
    const [open, setOpen] = React.useState(false);
    const [scrolled, setScrolled] = React.useState(false);

    React.useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 inset-x-0 z-40 transition-all duration-500 ${
                scrolled
                    ? "backdrop-blur-xl bg-[#faf9f6]/80 border-b border-[#e8e4db]"
                    : "bg-transparent"
            }`}
        >
            <div className="mx-auto max-w-7xl px-6 md:px-10 h-20 flex items-center justify-between">
                <a
                    href="#top"
                    data-testid="nav-logo"
                    className="flex items-center gap-3"
                >
                    <img
                        src={CLINIC_LOGO}
                        alt="Galaxy Dental Clinic"
                        className="h-10 w-10 md:h-11 md:w-11 object-contain"
                    />
                    <span className="flex flex-col leading-none">
                        <span className="font-serif-display text-2xl md:text-[26px] tracking-tight text-[#114b5f]">
                            Galaxy Dental
                        </span>
                        <span className="text-[9px] md:text-[10px] uppercase tracking-[0.32em] text-[#7a8582] mt-1">
                            Creating Beautiful Smiles
                        </span>
                    </span>
                </a>

                <nav className="hidden lg:flex items-center gap-8">
                    {NAV.map((n) => (
                        <a
                            key={n.href}
                            href={n.href}
                            data-testid={`nav-${n.label.toLowerCase()}`}
                            className="text-sm text-[#4a5250] hover:text-[#114b5f] transition-colors relative group"
                        >
                            {n.label}
                            <span className="absolute -bottom-1 left-0 w-0 h-px bg-[#d4af37] transition-all duration-300 group-hover:w-full" />
                        </a>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-3">
                    <a
                        href={`tel:${CLINIC.phoneRaw}`}
                        data-testid="nav-call"
                        className="text-sm text-[#4a5250] hover:text-[#114b5f]"
                    >
                        {CLINIC.phone}
                    </a>
                    <button
                        type="button"
                        onClick={onBook}
                        data-testid="nav-book-appointment-button"
                        className="rounded-full bg-[#114b5f] text-[#faf9f6] px-6 py-3 text-sm font-medium hover:bg-[#0d3a4a] transition-all hover:shadow-lg"
                    >
                        Book Appointment
                    </button>
                </div>

                <button
                    type="button"
                    onClick={() => setOpen((v) => !v)}
                    data-testid="nav-mobile-toggle"
                    aria-label="Toggle menu"
                    className="lg:hidden inline-flex items-center justify-center h-11 w-11 rounded-full border border-[#e8e4db] bg-white/70"
                >
                    <div className="flex flex-col gap-1.5">
                        <span
                            className={`h-px w-5 bg-[#1a1d1c] transition ${
                                open ? "translate-y-[7px] rotate-45" : ""
                            }`}
                        />
                        <span
                            className={`h-px w-5 bg-[#1a1d1c] transition ${
                                open ? "opacity-0" : ""
                            }`}
                        />
                        <span
                            className={`h-px w-5 bg-[#1a1d1c] transition ${
                                open ? "-translate-y-[7px] -rotate-45" : ""
                            }`}
                        />
                    </div>
                </button>
            </div>

            {open && (
                <div className="lg:hidden bg-[#faf9f6] border-t border-[#e8e4db]">
                    <div className="px-6 py-6 flex flex-col gap-4">
                        {NAV.map((n) => (
                            <a
                                key={n.href}
                                href={n.href}
                                onClick={() => setOpen(false)}
                                data-testid={`nav-mobile-${n.label.toLowerCase()}`}
                                className="text-base text-[#1a1d1c]"
                            >
                                {n.label}
                            </a>
                        ))}
                        <button
                            type="button"
                            onClick={() => {
                                setOpen(false);
                                onBook?.();
                            }}
                            data-testid="nav-mobile-book-appointment-button"
                            className="rounded-full bg-[#114b5f] text-[#faf9f6] px-6 py-3 text-sm font-medium"
                        >
                            Book Appointment
                        </button>
                    </div>
                </div>
            )}
        </header>
    );
}
